import { useEffect, useState } from "react";
import { useUser } from "@/hooks/useUser";
import { fetchUnreadMessageCount, isMissingProfileError } from "@/lib/users-api";
import { onMessagesChanged, onConversationReadAcrossTabs } from "@/lib/messages-events";
import { getSupabaseClient } from "@/lib/supabase/client";
import { subscribeHeaderBadges } from "@/lib/supabase/header-badge-subscription";
import { Sentry } from "@/lib/sentry";

/** Account-wide message count only; dashboard chrome replaces the public header. */
export function useDashboardUnreadMessages(): number {
  const { user, session } = useUser();
  const userId = user?.id;
  const token = session?.access_token;
  const [result, setResult] = useState<{ userId: string; token: string; count: number } | null>(null);

  useEffect(() => {
    setResult(null);
    if (!userId || !token) return;
    let disposed = false;
    let request = 0;
    const refresh = () => {
      if (disposed) return;
      const current = ++request;
      void fetchUnreadMessageCount(token).then(({ count }) => {
        if (!disposed && current === request) setResult({ userId, token, count });
      }).catch((error: unknown) => {
        if (disposed || current !== request) return;
        const e = error as { status?: number; message?: string } | null;
        const expected = isMissingProfileError(error) || e?.status === 401 ||
          /Authentication required/i.test(e?.message ?? "");
        if (expected) setResult({ userId, token, count: 0 });
        else Sentry.captureException(error);
      });
    };
    refresh();
    const stopLocal = onMessagesChanged(refresh);
    const stopAcrossTabs = onConversationReadAcrossTabs(refresh);
    const client = getSupabaseClient();
    let stopRealtime: (() => void) | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;
    if (client) {
      try {
        stopRealtime = subscribeHeaderBadges(client, userId, refresh);
      } catch (error) {
        // A badge must never prevent the rest of the dashboard from mounting.
        Sentry.captureException(error);
        interval = setInterval(refresh, 30_000);
      }
    } else {
      interval = setInterval(refresh, 30_000);
    }
    return () => {
      disposed = true;
      stopLocal();
      stopAcrossTabs();
      stopRealtime?.();
      if (interval !== undefined) clearInterval(interval);
    };
  }, [userId, token]);

  // Mask synchronously on identity/token changes, before effect cleanup runs.
  return userId && token && result?.userId === userId && result.token === token ? result.count : 0;
}
